import { GraphQLError } from 'graphql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthenticationContext } from '@speeki/firebase-chat';

import {
    ChatUserDocument,
    ChatUserQuery,
    ChatUserQueryVariables,
} from '@graphql/generated/graphql';

import { sentry } from '@services/Sentry';

import { client } from '@boot/apolloClient';

//NOTE: Temporary workaround
type ReturnedState =
    | {
          data: ChatUserQuery;
          errors?: readonly GraphQLError[];
      }
    | undefined;

type ReturnedFunction = (
    variables: ChatUserQueryVariables,
) => Promise<ChatUserQuery>;

export const useGetChatUser = (): [ReturnedFunction, ReturnedState] => {
    const { t } = useTranslation();
    const { authMetadata } = useAuthenticationContext();
    const [state, setSate] = useState<{
        data: ChatUserQuery;
        errors?: readonly GraphQLError[];
    }>();

    const getUserByUuid = async (
        variables: ChatUserQueryVariables,
    ): Promise<ChatUserQuery> => {
        try {
            const { data, errors } = await client.query({
                context: { firebaseToken: authMetadata?.token },
                fetchPolicy: 'cache-first',
                query: ChatUserDocument,
                variables: variables,
            });

            const {
                getUserByUuid: { firstName, lastName, name },
            } = data;

            if (!firstName || !lastName) {
                const [_firstName, _lastName] = name?.split(' ', 2) ?? [
                    t('UNKNOWN'),
                    t('USER'),
                ];

                setSate({
                    data: {
                        getUserByUuid: {
                            ...data.getUserByUuid,
                            firstName: _firstName,
                            lastName: _lastName,
                        },
                    },
                    errors,
                });
                return {
                    getUserByUuid: {
                        ...data.getUserByUuid,
                        firstName: _firstName,
                        lastName: _lastName,
                    },
                };
            }

            setSate({
                data,
                errors,
            });

            return data;
        } catch (e) {
            sentry.captureException(e);
            // TODO: REPAIR ANY TYPE
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any
            return e as any;
        }
    };

    return [getUserByUuid, state];
};
